// Home metric-strip data layer: the two account-wide aggregate reads behind the
// canvas strip (PR 6 of epic #4195).
//
//   GET /v1/overview/counts — repos enabled, open PRs, PRs merged since the
//                             standup cutoff, pending approvals (`read:prs`)
//   GET /v1/overview/burn   — agent-session spend over the last hour, with the
//                             count of sessions that carry no price (`read:sessions`)
//
// Two routes because each takes ONE scope: a token holding only `read:prs`
// gets the counts and a 403 on the burn, and the strip renders that tile as the
// unset marker (`UNSET`) rather than guessing (`overview-tiles.ts`). Snake_case mirrors the
// wire (`packages/domain/src/wire/overview.ts`), and each body is PARSED through
// its domain schema, not cast: a route that drifts fails loudly here rather than
// painting `undefined` into a tile. Both are polled: neither aggregate rides the account stream, and
// both windows move with no event at all. `merged_24h` (wire name kept) is a DAY
// window, not a rolling 24h: it counts from `merged_since_iso`, the most recent
// 14:00 UTC (22:00 Manila, the team standup), so the tile agrees with the standup
// and resets to zero at that cutoff. The burn is a rolling last hour.

import {
  type OverviewBurnWire,
  OverviewBurnWireSchema,
  type OverviewCountsWire,
  OverviewCountsWireSchema,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** `GET /v1/overview/counts` — the server's own wire type, not a mirror. */
export type OverviewCounts = OverviewCountsWire;

/**
 * `GET /v1/overview/burn`. `cost_usd` is a decimal string (no float rounds a
 * bill) and null when no session in the window carried a price.
 */
export type OverviewBurn = OverviewBurnWire;

export const OVERVIEW_COUNTS_QUERY_KEY = 'overview_counts';
export const OVERVIEW_BURN_QUERY_KEY = 'overview_burn';

/** Time windows with no stream topic: the strip re-reads on the overview's 30s cadence. */
const POLL_MS = 30_000;

/** The account's overview counts. Idle with no session. */
export function useOverviewCounts(token: string | null = SESSION_TOKEN) {
  return createQuery<OverviewCounts>(() => ({
    queryKey: [OVERVIEW_COUNTS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    refetchInterval: POLL_MS,
    queryFn: () =>
      apiFetch('/v1/overview/counts', token, undefined, { schema: OverviewCountsWireSchema }),
  }));
}

/** The account's last-hour token burn. Idle with no session. */
export function useOverviewBurn(token: string | null = SESSION_TOKEN) {
  return createQuery<OverviewBurn>(() => ({
    queryKey: [OVERVIEW_BURN_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    refetchInterval: POLL_MS,
    queryFn: () =>
      apiFetch('/v1/overview/burn', token, undefined, { schema: OverviewBurnWireSchema }),
  }));
}
